/** @jsxImportSource @emotion/react */

import {
  CalciteInput,
  CalciteInputMessage,
  CalciteLabel,
} from "@esri/calcite-components-react";
import { useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import { InputProps } from "./Input.d";

const Input: React.FC<InputProps> = ({
  label,
  labelSize = "m",
  scale = "m",
  onChange,
  icon,
  value,
  type,
  wrapperCss,
  message,
  status = "valid",
  clearable,
  readonly,
  disabled,
  max,
  minLength,
  onKeyDown,
  showMessageOnFocus = false,
  copyable = true,
  focused = false,
  ...remainingProps
}) => {
  const calciteInput = useRef<HTMLCalciteInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (focused && calciteInput.current) calciteInput.current.setFocus();
  }, [focused]);

  const shouldShowMessage = showMessageOnFocus ? isFocused && message : message;

  return (
    <>
      <CalciteLabel
        scale={labelSize}
        css={[wrapperCss, copyable && tw`cursor-pointer select-text`]}
        onClick={
          copyable
            ? (e) => {
                if (
                  calciteInput.current &&
                  calciteInput.current.contains(e.target as Node)
                ) {
                  return;
                }
                navigator.clipboard.writeText(
                  calciteInput.current?.value || "",
                );
              }
            : undefined
        }
      >
        {label}
        <CalciteInput
          status={status}
          icon={icon}
          iconEnd={undefined}
          ref={calciteInput}
          clearable={clearable}
          type={type}
          value={value}
          maxLength={max}
          minLength={minLength}
          onCalciteInputInput={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          scale={scale}
          readOnly={readonly}
          style={{
            opacity: readonly ? "1" : disabled ? ".5" : "1",
          }}
          {...(remainingProps as any)}
        >
          {value}
        </CalciteInput>
        {shouldShowMessage && (
          <CalciteInputMessage
            css={[tw`cursor-default`, status === "invalid" && tw`text-red-500`]}
            status={status}
          >
            {message}
          </CalciteInputMessage>
        )}
      </CalciteLabel>
    </>
  );
};

export default Input;
